// City Configuration Data Model for Agoda Integration
export const cityConfigModel = {
  // City Configuration Collection Structure
  collection: 'cityConfigurations',
  
  // Default City Configuration Document Structure
  defaultDocument: {
    id: '', // Auto-generated by Firestore
    cityName: '', // ชื่อเมือง (ภาษาไทย)
    cityNameEn: '', // ชื่อเมือง (ภาษาอังกฤษ)
    countryCode: 'TH', // รหัสประเทศ (TH, MY, SG, etc.)
    countryName: 'Thailand', // ชื่อประเทศ
    
    // Geographic Information
    latitude: 0, // ละติจูด
    longitude: 0, // ลองจิจูด
    timezone: 'Asia/Bangkok', // เขตเวลา
    
    // Agoda Integration Settings
    agodaSettings: {
      cityId: '', // Agoda City ID
      isActive: true, // เปิดใช้งานการค้นหาจาก Agoda
      searchRadius: 50, // รัศมีการค้นหา (กิโลเมตร)
      defaultLanguage: 'th', // ภาษาที่ใช้ในการค้นหา
      currency: 'THB', // สกุลเงิน
      maxResults: 20 // จำนวนผลลัพธ์สูงสุด
    },
    
    // Property Types Configuration
    propertyTypes: {
      hotels: {
        enabled: true,
        priority: 1,
        categories: ['hotel', 'resort', 'boutique', 'luxury']
      },
      restaurants: {
        enabled: true,
        priority: 2,
        categories: ['restaurant', 'cafe', 'fine_dining', 'street_food']
      },
      attractions: {
        enabled: false,
        priority: 3,
        categories: ['attraction', 'museum', 'park', 'temple']
      }
    },
    
    // Status and Metadata
    status: 'active', // active, inactive, pending
    isDefault: false, // เป็นเมืองเริ่มต้นหรือไม่
    createdAt: '', // ISO string
    updatedAt: '', // ISO string
    createdBy: '', // User ID who created this config
    updatedBy: '', // User ID who last updated this config
    
    // Statistics
    stats: {
      totalSearches: 0, // จำนวนการค้นหาทั้งหมด
      totalBookings: 0, // จำนวนการจองทั้งหมด
      totalRevenue: 0, // รายได้รวม
      lastSearchDate: null, // วันที่ค้นหาล่าสุด
      lastBookingDate: null // วันที่จองล่าสุด
    }
  },
  
  // City Status Options
  statusOptions: [
    { value: 'active', label: 'ใช้งาน', color: 'bg-green-100 text-green-800' },
    { value: 'inactive', label: 'ไม่ใช้งาน', color: 'bg-gray-100 text-gray-800' },
    { value: 'pending', label: 'รอการอนุมัติ', color: 'bg-yellow-100 text-yellow-800' }
  ],
  
  // Property Type Options
  propertyTypeOptions: [
    { value: 'hotels', label: 'โรงแรม', icon: '🏨', description: 'โรงแรม รีสอร์ท และที่พัก' },
    { value: 'restaurants', label: 'ร้านอาหาร', icon: '🍽️', description: 'ร้านอาหาร คาเฟ่ และสถานที่รับประทานอาหาร' },
    { value: 'attractions', label: 'สถานที่ท่องเที่ยว', icon: '🎯', description: 'สถานที่ท่องเที่ยว พิพิธภัณฑ์ และกิจกรรม' }
  ],
  
  // Validation Rules
  validation: {
    cityName: {
      required: true,
      minLength: 2,
      maxLength: 100,
      message: 'ชื่อเมืองต้องมี 2-100 ตัวอักษร'
    },
    cityNameEn: {
      required: true,
      minLength: 2,
      maxLength: 100,
      message: 'ชื่อเมืองภาษาอังกฤษต้องมี 2-100 ตัวอักษร'
    },
    countryCode: {
      required: true,
      length: 2,
      message: 'รหัสประเทศต้องมี 2 ตัวอักษร'
    },
    latitude: {
      required: true,
      min: -90,
      max: 90,
      message: 'ละติจูดต้องอยู่ระหว่าง -90 ถึง 90'
    },
    longitude: {
      required: true,
      min: -180,
      max: 180,
      message: 'ลองจิจูดต้องอยู่ระหว่าง -180 ถึง 180'
    },
    agodaSettings: {
      cityId: {
        required: true,
        message: 'กรุณาระบุ Agoda City ID'
      },
      searchRadius: {
        required: true,
        min: 1,
        max: 200,
        message: 'รัศมีการค้นหาต้องอยู่ระหว่าง 1-200 กิโลเมตร'
      },
      maxResults: {
        required: true,
        min: 1,
        max: 100,
        message: 'จำนวนผลลัพธ์สูงสุดต้องอยู่ระหว่าง 1-100'
      }
    }
  }
};

// Helper Functions
export const createCityConfig = (data = {}) => {
  const now = new Date().toISOString();
  
  return {
    ...cityConfigModel.defaultDocument,
    ...data,
    createdAt: now,
    updatedAt: now,
    // Initialize stats with default values
    stats: {
      totalSearches: 0,
      totalBookings: 0,
      totalRevenue: 0,
      lastSearchDate: null,
      lastBookingDate: null
    }
  };
};

export const validateCityConfig = (cityConfig) => {
  const errors = {};
  const rules = cityConfigModel.validation;
  
  // Validate city name
  if (!cityConfig.cityName || cityConfig.cityName.length < rules.cityName.minLength) {
    errors.cityName = rules.cityName.message;
  }
  
  // Validate city name in English
  if (!cityConfig.cityNameEn || cityConfig.cityNameEn.length < rules.cityNameEn.minLength) {
    errors.cityNameEn = rules.cityNameEn.message;
  }
  
  // Validate country code
  if (!cityConfig.countryCode || cityConfig.countryCode.length !== rules.countryCode.length) {
    errors.countryCode = rules.countryCode.message;
  }
  
  // Validate latitude
  if (cityConfig.latitude === undefined || cityConfig.latitude < rules.latitude.min || cityConfig.latitude > rules.latitude.max) {
    errors.latitude = rules.latitude.message;
  }
  
  // Validate longitude
  if (cityConfig.longitude === undefined || cityConfig.longitude < rules.longitude.min || cityConfig.longitude > rules.longitude.max) {
    errors.longitude = rules.longitude.message;
  }
  
  // Validate Agoda settings
  if (!cityConfig.agodaSettings?.cityId) {
    errors['agodaSettings.cityId'] = rules.agodaSettings.cityId.message;
  }
  
  if (cityConfig.agodaSettings?.searchRadius < rules.agodaSettings.searchRadius.min || 
      cityConfig.agodaSettings?.searchRadius > rules.agodaSettings.searchRadius.max) {
    errors['agodaSettings.searchRadius'] = rules.agodaSettings.searchRadius.message;
  }
  
  if (cityConfig.agodaSettings?.maxResults < rules.agodaSettings.maxResults.min || 
      cityConfig.agodaSettings?.maxResults > rules.agodaSettings.maxResults.max) {
    errors['agodaSettings.maxResults'] = rules.agodaSettings.maxResults.message;
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

export const formatCityConfigForDisplay = (cityConfig) => {
  const status = cityConfigModel.statusOptions.find(s => s.value === cityConfig.status);
  
  return {
    ...cityConfig,
    statusDisplay: status ? status.label : 'ไม่ใช้งาน',
    statusColor: status ? status.color : 'bg-gray-100 text-gray-800',
    formattedLocation: `${cityConfig.cityName}, ${cityConfig.countryName}`,
    formattedCoordinates: `${cityConfig.latitude.toFixed(4)}, ${cityConfig.longitude.toFixed(4)}`,
    enabledPropertyTypes: Object.entries(cityConfig.propertyTypes || {})
      .filter(([key, config]) => config.enabled)
      .map(([key, config]) => cityConfigModel.propertyTypeOptions.find(opt => opt.value === key))
      .filter(Boolean)
  };
};

// Predefined Thai Cities
export const predefinedThaiCities = [
  {
    cityName: 'กรุงเทพมหานคร',
    cityNameEn: 'Bangkok',
    countryCode: 'TH',
    countryName: 'Thailand',
    latitude: 13.7563,
    longitude: 100.5018,
    timezone: 'Asia/Bangkok',
    agodaSettings: {
      cityId: 'bangkok',
      isActive: true,
      searchRadius: 50,
      defaultLanguage: 'th',
      currency: 'THB',
      maxResults: 20
    },
    propertyTypes: {
      hotels: { enabled: true, priority: 1, categories: ['hotel', 'resort', 'boutique', 'luxury'] },
      restaurants: { enabled: true, priority: 2, categories: ['restaurant', 'cafe', 'fine_dining', 'street_food'] },
      attractions: { enabled: true, priority: 3, categories: ['attraction', 'museum', 'park', 'temple'] }
    },
    isDefault: true
  },
  {
    cityName: 'เชียงใหม่',
    cityNameEn: 'Chiang Mai',
    countryCode: 'TH',
    countryName: 'Thailand',
    latitude: 18.7883,
    longitude: 98.9853,
    timezone: 'Asia/Bangkok',
    agodaSettings: {
      cityId: 'chiang-mai',
      isActive: true,
      searchRadius: 30,
      defaultLanguage: 'th',
      currency: 'THB',
      maxResults: 15
    },
    propertyTypes: {
      hotels: { enabled: true, priority: 1, categories: ['hotel', 'resort', 'boutique'] },
      restaurants: { enabled: true, priority: 2, categories: ['restaurant', 'cafe', 'street_food'] },
      attractions: { enabled: true, priority: 3, categories: ['attraction', 'temple', 'park'] }
    }
  },
  {
    cityName: 'ภูเก็ต',
    cityNameEn: 'Phuket',
    countryCode: 'TH',
    countryName: 'Thailand',
    latitude: 7.8804,
    longitude: 98.3923,
    timezone: 'Asia/Bangkok',
    agodaSettings: {
      cityId: 'phuket',
      isActive: true,
      searchRadius: 40,
      defaultLanguage: 'th',
      currency: 'THB',
      maxResults: 15
    },
    propertyTypes: {
      hotels: { enabled: true, priority: 1, categories: ['hotel', 'resort', 'luxury'] },
      restaurants: { enabled: true, priority: 2, categories: ['restaurant', 'cafe', 'fine_dining'] },
      attractions: { enabled: true, priority: 3, categories: ['attraction', 'beach', 'island'] }
    }
  },
  {
    cityName: 'พัทยา',
    cityNameEn: 'Pattaya',
    countryCode: 'TH',
    countryName: 'Thailand',
    latitude: 12.9236,
    longitude: 100.8825,
    timezone: 'Asia/Bangkok',
    agodaSettings: {
      cityId: 'pattaya',
      isActive: true,
      searchRadius: 25,
      defaultLanguage: 'th',
      currency: 'THB',
      maxResults: 15
    },
    propertyTypes: {
      hotels: { enabled: true, priority: 1, categories: ['hotel', 'resort'] },
      restaurants: { enabled: true, priority: 2, categories: ['restaurant', 'cafe'] },
      attractions: { enabled: true, priority: 3, categories: ['attraction', 'beach'] }
    }
  },
  {
    cityName: 'สมุย',
    cityNameEn: 'Koh Samui',
    countryCode: 'TH',
    countryName: 'Thailand',
    latitude: 9.5018,
    longitude: 100.0000,
    timezone: 'Asia/Bangkok',
    agodaSettings: {
      cityId: 'koh-samui',
      isActive: true,
      searchRadius: 20,
      defaultLanguage: 'th',
      currency: 'THB',
      maxResults: 10
    },
    propertyTypes: {
      hotels: { enabled: true, priority: 1, categories: ['hotel', 'resort', 'luxury'] },
      restaurants: { enabled: true, priority: 2, categories: ['restaurant', 'cafe'] },
      attractions: { enabled: true, priority: 3, categories: ['attraction', 'beach', 'island'] }
    }
  }
];

// Helper function to get predefined city by name
export const getPredefinedCity = (cityName) => {
  return predefinedThaiCities.find(city => 
    city.cityName === cityName || city.cityNameEn === cityName
  );
};

// Helper function to get all predefined cities
export const getAllPredefinedCities = () => {
  return predefinedThaiCities;
};

// Helper function to check if city is predefined
export const isPredefinedCity = (cityName) => {
  return predefinedThaiCities.some(city => 
    city.cityName === cityName || city.cityNameEn === cityName
  );
};

